import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, createVNode as _createVNode, withModifiers as _withModifiers, resolveComponent as _resolveComponent, withCtx as _withCtx, normalizeClass as _normalizeClass, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = ["for"]
const _hoisted_2 = { class: "text" }
const _hoisted_3 = ["onMousedown"]

import { VIconSvg } from "@/components";
import VErrorTooltip from "../VErrorTooltip/VErrorTooltip.vue";
import { isEqual } from "lodash";
import { getRandomId } from "@/mixins/global.mixin";
import { ref, computed, nextTick, watch } from "vue";
import { TranslateResult } from "vue-i18n";
import i18n from "@/plugins/vue-i18n";

interface Props {
  value?:
    | string
    | number
    | boolean
    | { label: string | TranslateResult; code: string | number | boolean };
  options?: Array<{
    label?: string | TranslateResult;
    code?: string | number | boolean;
  }>;
  label?: string | TranslateResult;
  labelPrepend?: boolean;
  itemLabel?: string;
  trackBy?: string;
  placeholder?: string | TranslateResult;
  size?: string;
  variant?: string;
  disabled?: boolean;
  allowEmpty?: boolean;
  error?: string | TranslateResult;
  required?: boolean;
  description?: string | TranslateResult;
  preselectFirst?: boolean;
  hidePlaceholder?: boolean;
  openDirection?: string;
  fullReturn?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'VInputSelect',
  props: {
    value: { type: [String, Number, Boolean, Object], default: "" },
    options: { default: () => [
    {
      label: "",
      code: "",
    },
  ] },
    label: { default: "" },
    labelPrepend: { type: Boolean, default: false },
    itemLabel: { default: "label" },
    trackBy: { default: "code" },
    placeholder: { default: "" },
    size: { default: "md" },
    variant: { default: "regular" },
    disabled: { type: Boolean, default: false },
    allowEmpty: { type: Boolean, default: true },
    error: { default: "" },
    required: { type: Boolean, default: false },
    description: { default: "" },
    preselectFirst: { type: Boolean, default: false },
    hidePlaceholder: { type: Boolean, default: false },
    openDirection: { default: "" },
    fullReturn: { type: Boolean, default: false }
  },
  emits: ["input", "open", "searchChange"],
  setup(__props: any, { emit: __emit }) {

const emit = __emit;

const multiselect = ref();

const props = __props;

const touchStartedAt = ref(null);

const id = computed(() => getRandomId());
const isStartedTouchEvent = ref(false);
const localValue = ref("");

const trackByPropValue = computed(() => {
  return props.fullReturn ? localValue.value[props.trackBy] : localValue.value;
});

const selectValue = computed({
  get() {
    return props.options.find((option) => {
      const isDeepCheckNeeded = ["object", "array"].includes(typeof trackByPropValue.value);

      return isDeepCheckNeeded
        ? isEqual(option[props.trackBy], trackByPropValue.value)
        : option[props.trackBy] === trackByPropValue.value;
    });
  },

  set(value) {
    if (value?.[props.trackBy] !== trackByPropValue.value) {
      const eventValue = props.fullReturn ? value : value?.[props.trackBy];

      localValue.value = eventValue;
      emit("input", eventValue);
    }
  },
});

const inputWrapperClasses = computed(() => {
  const variant = `input-${props.variant}`;
  const size = `input-${props.variant}-size-${props.size}`;
  const classes = {
    "input-error": props.error,
    "input-required": props.required,
    "input-label-preppend": props.labelPrepend,
  };

  return [variant, size, classes];
});

const isShownInfoIcon = computed(() => props.variant === "regular" && !!props.description);

const isShownLabel = computed(() => {
  let show = !!props.label.length;

  if (props.variant === "inner") {
    if (!trackByPropValue.value?.toString().length) {
      show = false;
    }
  }

  return show;
});

const placeholderText = computed(() => {
  let placeholder: string | TranslateResult = "";

  if (!props.hidePlaceholder) {
    placeholder = props.placeholder ? props.placeholder : i18n.t("placeholder.selectOne");
  }

  return placeholder;
});

const initSelectTapEvent = () => {
  nextTick(() => {
    const [optionsWrapper] = multiselect.value.$refs.list.children;

    Array.from(optionsWrapper.children).map((element, index) => {
      if (element.className === "multiselect__element") {
        const [option] = element.children;

        option.addEventListener("touchstart", () => {
          touchStartedAt.value = Date.now();
          multiselect.value.pointerSet(index);
        });

        option.addEventListener("touchend", () => {
          const value = props.options[index];

          const duration = Date.now() - props.touchStartedAt;

          if (!isEqual(value, selectValue) && duration <= 150) {
            selectValue.value = value;
          }
        });
      }
    });
  });
};

const onClickWrapper = () => {
  const { isOpen, activate } = multiselect.value;

  if (!isStartedTouchEvent.value) {
    initSelectTapEvent();
    isStartedTouchEvent.value = true;
  }

  if (!isOpen) activate();
};

const onClickOutsideWrapper = () => {
  const { isOpen, deactivate } = multiselect.value;

  if (isOpen) deactivate();
};

const onOpenMultiselect = () => {
  emit("open");
};

const onSearchChange = (value: string) => {
  emit("searchChange", value);
};

watch(
  () => props.value,
  () => {
    localValue.value = props.value;
  },
  { immediate: true, deep: true }
);

return (_ctx: any,_cache: any) => {
  const _component_VueMultiselect = _resolveComponent("VueMultiselect")!
  const _directive_touch = _resolveDirective("touch")!
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input-wrapper", inputWrapperClasses.value])
  }, [
    (isShownLabel.value)
      ? (_openBlock(), _createElementBlock("label", {
          key: 0,
          class: "label",
          for: id.value
        }, [
          _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.label), 1),
          (isShownInfoIcon.value)
            ? (_openBlock(), _createBlock(_unref(VIconSvg), {
                key: 0,
                tooltip: _ctx.description,
                name: "InfoCircle2",
                color: "fc-stroke",
                size: "xs"
              }, null, 8, ["tooltip"]))
            : _createCommentVNode("", true)
        ], 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createVNode(_component_VueMultiselect, {
      id: id.value,
      ref_key: "multiselect",
      ref: multiselect,
      modelValue: selectValue.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectValue).value = $event)),
      options: _ctx.options,
      label: _ctx.itemLabel,
      "track-by": _ctx.trackBy,
      placeholder: placeholderText.value,
      disabled: _ctx.disabled,
      "allow-empty": _ctx.allowEmpty,
      "preselect-first": _ctx.preselectFirst,
      "selected-label": "",
      "select-label": "",
      "deselect-label": "",
      class: "multiselect",
      "open-direction": _ctx.openDirection,
      onOpen: onOpenMultiselect,
      onSearchChange: onSearchChange
    }, {
      caret: _withCtx(({ toggle }) => [
        _createElementVNode("div", {
          class: "multiselect-caret",
          onMousedown: _withModifiers(toggle, ["prevent","stop"])
        }, [
          _createVNode(_unref(VIconSvg), {
            name: "UnfoldMore",
            size: "xs",
            color: "fc-stroke",
            class: "icon-caret"
          })
        ], 40, _hoisted_3)
      ]),
      _: 1
    }, 8, ["id", "modelValue", "options", "label", "track-by", "placeholder", "disabled", "allow-empty", "preselect-first", "open-direction"]),
    (_ctx.error)
      ? (_openBlock(), _createBlock(VErrorTooltip, {
          key: 1,
          error: _ctx.error
        }, null, 8, ["error"]))
      : _createCommentVNode("", true)
  ], 2)), [
    [_directive_touch, onClickWrapper],
    [_directive_click_outside, onClickOutsideWrapper]
  ])
}
}

})